import { cn } from '@/lib/utils';
import {
  ALGO_USES,
  algoTradingVerdict,
  AlgoTradingVerdict,
} from '@/modules/propfirms/data/algo-trading';
import { AlgoLevel, Propfirm } from '@/modules/propfirms/data/types';
import { useDataText } from '@/modules/propfirms/data/use-data-text';
import { Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  MdBlock,
  MdHelpOutline,
  MdInfoOutline,
  MdOutlineWarningAmber,
} from 'react-icons/md';

const LEVEL_CLASS: Record<AlgoLevel, string> = {
  allowed: 'text-green-400',
  conditional: 'text-sky-400',
  forbidden: 'text-[#ef4444]',
  unknown: 'text-neutral-500',
};

/// Une condition à connaître n'est pas un danger : elle s'annonce en bleu,
/// l'orange est gardé pour un usage réellement fermé, le rouge pour tout fermé.
const VERDICT_STYLE: Record<
  Exclude<AlgoTradingVerdict, 'allowed'>,
  { icon: React.FC<{ size?: number }>; className: string }
> = {
  conditional: { icon: MdInfoOutline, className: 'text-sky-400' },
  partial: { icon: MdOutlineWarningAmber, className: 'text-[#f59e0b]' },
  forbidden: { icon: MdBlock, className: 'text-[#ef4444]' },
  unknown: { icon: MdHelpOutline, className: 'text-neutral-500' },
};

/// Le niveau d'un usage, en toutes lettres et en couleur.
export function AlgoLevelValue({ level }: { level: AlgoLevel }) {
  const { t } = useTranslation('propfirms');
  return <span className={LEVEL_CLASS[level]}>{t(`algo.level.${level}`)}</span>;
}

export interface AlgoTradingBadgeProps {
  firm: Propfirm;
  className?: string;
  size?: number;
}

/// Dit d'un coup d'œil ce que la propfirm laisse faire au terminal, au copieur
/// et aux stratégies automatisées, usage par usage dans l'info-bulle.
///
/// Rien ne s'affiche quand les trois usages passent sans réserve : une pastille
/// sur chaque ligne ne dirait plus rien.
export function AlgoTradingBadge({
  firm,
  className,
  size = 16,
}: AlgoTradingBadgeProps) {
  const { t } = useTranslation('propfirms');
  const dataText = useDataText();
  const { algo } = firm.rules;
  const verdict = algoTradingVerdict(algo);
  if (verdict === 'allowed') return null;

  const { icon: Icon, className: color } = VERDICT_STYLE[verdict];
  return (
    <Tooltip
      title={
        <div className="flex max-w-[320px] flex-col gap-1.5 text-[11px]">
          <span className="font-medium">{t(`algo.verdict.${verdict}`)}</span>
          <div className="flex flex-col gap-0.5">
            {ALGO_USES.map((use) => (
              <span key={use} className="flex justify-between gap-3">
                <span className="text-neutral-300">{t(`algo.use.${use}`)}</span>
                <AlgoLevelValue level={algo[use]} />
              </span>
            ))}
          </div>
          {algo.notes[0] && (
            <span className="text-neutral-400">{dataText(algo.notes[0])}</span>
          )}
        </div>
      }
    >
      <span className={cn('inline-flex items-center', color, className)}>
        <Icon size={size} />
      </span>
    </Tooltip>
  );
}
